import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { parseError } from 'utils/helpers';
import logo from './img/logo.svg'
import axios from 'axios'
import { useStore } from 'store'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import Stepper from 'components/Stepper/Stepper';
import GoogleSign from 'components/GoogleSign/GoogleSign';
import ConfirmationCode from 'components/ConfirmationCode/ConfirmationCode';

const UserSignup = () => {
    const navigate = useNavigate()
    const [step, setStep] = useState(0);
    const [termsAccept, setTermsAccept] = useState(false);
    const [termsError, setTermsError] = useState(false);
    const [isGoogleAuth, setIsGoogleAuth] = useState(false);

    const [otpSent, setOtpSent] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

    // Timer state
    const [timeLeft, setTimeLeft] = useState(0);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
    })
    const [name, setName] = useState('');
    const [surname, setSurname] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordRepeat, setPasswordRepeat] = useState('');
    const [cost, setCost] = useState([]);
    const [rate, setRate] = useState();
    const [dobState, setDobState] = useState('');
    const [addressState, setAddressState] = useState({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United States of America',
        countryCode: 'US',
    });
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);
    const [isAddressVerified, setIsAddressVerified] = useState(false);
    const [finishing, setFinishing] = useState(false);

    const [error, setError] = useState({})


    // Timer Logic
    useEffect(() => {
        if (timeLeft > 0) {
            const timerId = setTimeout(() => {
                setTimeLeft((prev) => prev - 1);
            }, 1000);
            return () => clearTimeout(timerId);
        }
    }, [timeLeft]);

    const store = useStore();
    const signInfo = useMemo(
        () => ({
            name,
            surname,
            email,
            password,
            rate,
            cost,
            dob: dobState,
            address: addressState,
            phone: phoneNumber,
            isPhoneVerified,
            isAddressVerified,
            code: otpCode
        }),
        [
            name,
            surname,
            email,
            password,
            rate,
            cost,
            dobState,
            addressState,
            phoneNumber,
            isPhoneVerified,
            isAddressVerified,
            otpCode
        ]
    );


    const googleSing = useCallback((data) => {
        if (data.email) {
            setIsGoogleAuth(true);
            setEmail(data.email);
            setPassword(null);
            if (data.given_name) setName(data.given_name);
            if (data.family_name) setSurname(data.family_name);
            setStep(2);
        }
    }, []);

    useEffect(() => {
        document.body.classList.add("tailwind-body");

        return () => {
            document.body.classList.remove("tailwind-body");
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))
    }

    const finishSignup = useCallback(() => {
        setFinishing(true);
        axios
            .post(`/auth/signup/customer`, signInfo)
            .then(({ data }) => {
                store.auth.authorize(data.token, 'customer');
                navigate('/athena/welcome');
            })
            .catch((e) => {
                setStep(0);
                setError(parseError(e));
                setFinishing(false);
            });
    }, [signInfo, navigate, store.auth]);

    const startSignup = useCallback((e) => {
        e.preventDefault()
        if (!termsAccept) {
            setTermsError(true)
            return
        } else {
            setTermsError(false)
        }

        if (!isGoogleAuth && signInfo.password !== passwordRepeat) {
            return setError({
                type: 'error',
                message: 'Passwords do not match'
            })
        }


        axios
            .post(`/auth/signup/customer/validate`, signInfo)
            .then(() => {
                if (isGoogleAuth) {
                    setStep(2);
                } else {
                    setOtpSent(true);
                    setError({});
                    setTimeLeft(60);
                }
            })
            .catch((e) => {
                setError(parseError(e));
            });
    }, [
        passwordRepeat,
        isGoogleAuth,
        termsAccept,
        signInfo
    ]);

    const verifyEmailCode = useCallback((e) => {
        e.preventDefault();

        // Validation for empty code
        if (!otpCode || otpCode.length < 4) {
            return setError({ message: 'Please enter a valid code' });
        }

        setIsVerifyingOtp(true);
        setError({});

        axios
            .post('/auth/signup/verify/enter', {
                email: signInfo.email,
                code: otpCode.join(''),
            })
            .then(() => {
                setOtpSent(false);
                setStep(1);
            })
            .catch((e) => {
                setError(parseError(e));
                setOtpCode('');
            })
            .finally(() => {
                setIsVerifyingOtp(false);
            });
    }, [signInfo.email, otpCode]);

    const resendEmailConfirmation = useCallback(() => {
        setIsVerifyingOtp(true);
        setError({});

        axios
            .post('/auth/signup/verify/send', { email: signInfo.email })
            .then(() => {
                setTimeLeft(60);
            })
            .catch((e) => {
                setError(parseError(e));
            })
            .finally(() => {
                setIsVerifyingOtp(false);
            });
    }, [signInfo.email]);

    const selectCategory = useCallback((cat) => {
        setCost((cost) => {
            const index = cost.findIndex((cost) => cost.title === cat);

            if (index >= 0) {
                const updatedCost = [...cost.slice(0, index), ...cost.slice(index + 1)];
                return updatedCost;
            } else {
                const newCost = { title: cat, price: '' };
                return [...cost, newCost];
            }
        });
    }, []);

    const setCategoryRate = useCallback((cat, price) => {
        setCost((cats) => {
            const index = cats.findIndex((item) => item.title === cat);
            if (index >= 0) {
                cats[index] = { title: cat, price };
            }
            return [...cats];
        });
    }, []);

    const onAddressChange = useCallback((name, value) => {
        setAddressState((previousState) => ({
            ...previousState,
            [name]: value,
        }));
    }, []);

    const onDobChange = useCallback((dob) => {
        setDobState(dob);
    }, []);

    const onNextStep = useCallback(() => {
        if (step >= 1) {
            return finishSignup();
        } else {
            setStep((step) => step + 1);
        }
    }, [step, finishSignup]);

    const handleBack = useCallback(() => {
        if (otpSent) {
            setOtpSent(false);
            return;
        }
        navigate(-1);
    }, [navigate, otpSent]);

    return (
        <div className="relative min-h-screen bg-gray-50 flex flex-col">
            <button
                type="button"
                onClick={handleBack}
                className="absolute top-6 left-6 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-sm font-medium text-gray-600 shadow ring-1 ring-gray-200 transition hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M15 18l-6-6 6-6" />
                </svg>
                <span>Back</span>
            </button>

            <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                {step === 0 && !otpSent && (
                    <>
                        <div className="sm:mx-auto sm:w-full sm:max-w-md">
                            <Link to="/" className="flex justify-center items-center space-x-2 mb-8">
                                <img
                                    src={logo}
                                    alt="Emissary Care"
                                    className="h-12 w-auto"
                                />
                            </Link>
                            <h2 className="mt-6 text-center text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                                Create your account
                            </h2>
                            <p className="mt-2 text-center text-sm text-gray-600">
                                Start your journey to better care today
                            </p>
                        </div>

                        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                                <form className="space-y-6" onSubmit={startSignup}>
                                    <GoogleSign onUserInfo={googleSing} type="up" />
                                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                        <div>
                                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                                                First name
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    id="firstName"
                                                    name="firstName"
                                                    type="text"
                                                    required
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    className="input-primary"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                                                Last name
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    id="lastName"
                                                    name="lastName"
                                                    type="text"
                                                    required
                                                    value={surname}
                                                    onChange={(e) => setSurname(e.target.value)}
                                                    className="input-primary"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                            Email address
                                        </label>
                                        <div className="mt-1">
                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                autoComplete="email"
                                                required
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="input-primary"
                                            />
                                        </div>
                                    </div>

                                    {!isGoogleAuth && (
                                        <>
                                            <div>
                                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                                    Password
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        id="password"
                                                        name="password"
                                                        type="password"
                                                        autoComplete="new-password"
                                                        required
                                                        value={password}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        className="input-primary"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                                    Confirm password
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        id="confirmPassword"
                                                        name="confirmPassword"
                                                        type="password"
                                                        autoComplete="new-password"
                                                        required
                                                        value={passwordRepeat}
                                                        onChange={(e) => setPasswordRepeat(e.target.value)}
                                                        className="input-primary"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <div className="h-[20px]">
                                        {error.message && (
                                            <div className="text-red-600 text-sm text-center">
                                                <b>{error.type}</b> {error.message}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-6 ">
                                        <label
                                            className={classNames(
                                                'flex items-start space-x-3 text-sm font-semibold text-gray-700 justify-center',
                                                { 'text-red-600': termsError }
                                            )}
                                        >
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={termsAccept}
                                                    onChange={(e) => {
                                                        setTermsAccept(e.target.checked)
                                                    }}
                                                    className=" h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <label htmlFor="emailNotifications" className="ml-2 block text-sm text-gray-900">
                                                    <div className="check"></div>I agree to the{' '}
                                                    <Link
                                                        to="/terms"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >Terms</Link> and{' '}
                                                    <Link
                                                        to="/policy"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >Privacy Policy</Link>
                                                </label>
                                            </div>
                                        </label>
                                    </div>
                                    <div>
                                        <button disabled={!termsAccept} type="submit" className="w-full btn-primary">
                                            Sign up
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </>
                )}

                {step === 0 && otpSent && (
                    <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Check your email</h3>
                            <p className="text-sm text-gray-600 mb-6">
                                We've sent instructions to <b>{email}</b>.<br />
                                Please enter the verification code to continue.
                            </p>

                            <form onSubmit={verifyEmailCode} className="space-y-6">

                                <ConfirmationCode setConfirm={setOtpCode} />

                                <div className="h-[20px]">
                                    {error.message && (
                                        <div className="text-red-600 text-sm">
                                            {error.message}
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="w-full btn-primary"
                                    disabled={isVerifyingOtp}
                                >
                                    {isVerifyingOtp ? 'Verifying...' : 'Verify & Continue'}
                                </button>

                                <div className="text-sm mt-4">
                                    {timeLeft > 0 ? (
                                        <p className="text-gray-400">Resend code in {timeLeft}s</p>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={resendEmailConfirmation}
                                            disabled={isVerifyingOtp}
                                            className="text-blue-600 hover:text-blue-500 font-medium"
                                        >
                                            Resend Code
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {step > 0 && !finishing && (
                    <Stepper
                        step={step}
                        nextStep={onNextStep}
                        userType={'customer'}
                        signInfo={signInfo}
                        finish={finishSignup}
                        categories={cost}
                        selectCategory={selectCategory}
                        isGoogleAuth={isGoogleAuth}
                        dob={dobState}
                        onDobChange={onDobChange}
                        address={addressState}
                        onAddressChange={onAddressChange}
                        isAddressVerified={isAddressVerified}
                        setIsAddressVerified={setIsAddressVerified}
                        setCategoryRate={setCategoryRate}
                        setRate={setRate}
                        rate={rate}
                        setPhoneNumber={setPhoneNumber}
                        phoneNumber={phoneNumber}
                        setIsPhoneVerified={setIsPhoneVerified}
                    />
                )}
            </div>
        </div>
    )
}

export default observer(UserSignup);